/**
 * Pricing — MS30 public pricing page.
 *
 * Lucky's earlier call (S6a, when pricing was first removed from the
 * landing): "Anchoring users to fixed price tiers would limit their
 * scope on understanding what we are capable of doing — I want them
 * to feel open-ended."
 *
 * MS30 honours that brief while solving the B2B-buyer-leaves-because-
 * no-pricing problem: a single calm public page that frames the model
 * (pay-per-celebration, $99 standard, anything bigger we quote) but
 * deliberately doesn't lay out a rigid tier matrix. Two cards: the
 * standard model + the concierge route, with the second giving more
 * visual weight than the first to signal "talk to us for anything
 * that isn't routine."
 *
 * Structure:
 *   - Top nav (logo + back link)
 *   - Hero: eyebrow + Heidi H1 + lede
 *   - Two-card model (standard / concierge)
 *   - "What's included" rule list (calm + comprehensive)
 *   - FAQ (4-5 buyer questions)
 *   - Closing CTA
 *   - Footer
 */

import { Link } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import { ArrowRight, ArrowUp, Check, MessageCircle } from "lucide-react";
import { Logo } from "@/components/lembra/Logo";
import { PageMeta } from "@/components/lembra/PageMeta";
import { SkipToContent } from "@/components/lembra/SkipToContent";

const displayFamily = "Newsreader, Georgia, serif";
const monoFamily =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const sansFamily = "DM Sans, sans-serif";

/* ─── What's included list ──────────────────────────────────────── */

const INCLUDED = [
  "Vendor coordination (we brief them, they make and deliver)",
  "A signed card, hand-printed on cream stock",
  "Courier delivery, on the day, in the time window you pick",
  "Dietary handling: gluten-free, vegan, allergies, all of it",
  "Order tracking and day-of notifications",
  "One invoice, one payment, no per-vendor billing",
];

/* ─── FAQ items ──────────────────────────────────────────────────── */

const FAQ = [
  {
    q: "Is there a subscription or minimum spend?",
    a: "No. You only pay when a celebration ships. Skip a month, send 12 in a week, doesn't matter. There's no tier you have to be on, no base fee.",
  },
  {
    q: "What does the $99 actually cover?",
    a: "A standard celebration from the multi-vendor catalogue: cake, hamper, flowers, spa voucher, workshop voucher, coffee voucher, or experience voucher (depending on what you brief us with). Includes the gift, the signed card, courier or digital delivery, dietary coordination, and our concierge handling everything in between.",
  },
  {
    // MS245: explicit "what's your floor" question. The Ask AI widget
    // answers this dynamically off the live catalogue snapshot (MS240),
    // but this FAQ entry hits the SEO + on-page surfaces too.
    q: "What's the cheapest option you have?",
    a: "Our coordination tier starts at $99, but individual catalogue items go lower. Vouchers from $25 (Endota spa gift card) and small cakes / bouquets from around $45-$49. Filter by 'Under $30' or '$30-$60' in the Browse catalogue to see what's available right now. We're actively sourcing more sub-$30 options.",
  },
  {
    q: "What counts as 'bigger'?",
    a: "Anything outside the catalogue, anything multi-vendor (e.g. cake + flowers + voucher together), anything with custom curation, or premium-tier products. We quote those directly via the chat concierge, usually within the same conversation you start in.",
  },
  {
    q: "Do you charge a markup over the vendor?",
    a: "We charge for the coordination, not a percentage on top of every product. The price you see is the price you pay. Vendors are paid out of that.",
  },
  {
    q: "What if the recipient doesn't like it?",
    a: "Tell us. We'll rebrief and resend at our cost the first time, on the same vendor or a different one. After that we'll work it out.",
  },
  // MS354: removed the MS352 interstate-specific FAQ entry. Per Lucky's
  // call, /pricing should position Lembra as an Australia-wide platform
  // rather than surface the Melbourne-only restriction at the FAQ
  // level. Interstate-interest capture moves to the order-flow
  // address-entry step (MS355) — that's the right moment of truth +
  // the lowest-friction capture path.
];

/* ─── Page ───────────────────────────────────────────────────────── */

export default function Pricing() {
  // MS188: FAQPage + BreadcrumbList structured data.
  //
  // FAQPage is Google's most visible JSON-LD type — eligible posts
  // get an expandable FAQ panel directly in the search snippet,
  // which dramatically improves CTR for /pricing as a "what does
  // Lembra cost" query landing surface. Reuses the same FAQ array
  // the page renders, so the rich result can't drift from the
  // visible content (one source of truth).
  //
  // BreadcrumbList closes the "Lembra › Pricing" trail consistent
  // with MS181/MS182's pattern on vendor + blog surfaces.
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.a,
      },
    })),
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Lembra", item: "https://lembra.com.au" },
      { "@type": "ListItem", position: 2, name: "Pricing", item: "https://lembra.com.au/pricing" },
    ],
  };

  return (
    <>
      <PageMeta
        title="Pricing. Pay per celebration, no subscription. Lembra."
        description="Workplace celebrations from $99 per gift. No subscription, no minimums, no per-vendor billing. Standard model from the catalogue, anything bigger we quote in chat."
        path="/pricing"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      </Helmet>
      <SkipToContent />

      <div style={{ background: "#FAF6F0", minHeight: "100vh" }}>
        {/* ── Top nav ── */}
        <header className="px-6 md:px-8 pt-6 pb-2">
          <div
            className="flex items-center justify-between"
            style={{ maxWidth: 1180, margin: "0 auto" }}
          >
            <Logo size="sm" />
            <Link
              to="/"
              className="text-sm font-medium text-navy/70 hover:text-coral"
              style={{ fontFamily: sansFamily }}
            >
              ← Back to lembra.com.au
            </Link>
          </div>
        </header>

        {/* ── Hero ── */}
        <section
          id="main"
          className="px-6 md:px-8"
          style={{
            paddingTop: "clamp(48px, 6vw, 72px)",
            paddingBottom: "clamp(40px, 5vw, 56px)",
          }}
        >
          <div style={{ maxWidth: 880, margin: "0 auto" }}>
            <div
              style={{
                fontFamily: monoFamily,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(196,86,74,0.85)",
                marginBottom: 12,
              }}
            >
              Pricing · No subscription
            </div>
            <h1
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(2.4rem, 5vw, 3.6rem)",
                lineHeight: 1.05,
                letterSpacing: "-0.022em",
                color: "#1D3461",
                margin: 0,
                maxWidth: "20ch",
              }}
            >
              Pay per{" "}
              <em style={{ fontStyle: "italic", fontWeight: 500, color: "#C4564A" }}>
                celebration
              </em>
              .
            </h1>
            <p
              style={{
                fontFamily: sansFamily,
                fontSize: 17,
                lineHeight: 1.55,
                color: "rgba(45,41,38,0.72)",
                marginTop: 18,
                marginBottom: 0,
                maxWidth: "62ch",
              }}
            >
              No tiers, no minimums, no per-vendor billing. Standard celebrations are
              $99 from the multi-vendor catalogue. Anything bigger or custom? We
              quote it in chat and you decide.
            </p>
          </div>
        </section>

        {/* ── Two-card model ── */}
        <section
          className="px-6 md:px-8"
          style={{ paddingBottom: "clamp(56px, 7vw, 80px)" }}
        >
          <div
            className="grid gap-4 md:grid-cols-[1fr_1.05fr] md:gap-5"
            style={{ maxWidth: 1080, margin: "0 auto" }}
          >
            {/* Standard model */}
            <article
              className="flex flex-col rounded-2xl bg-white p-7 md:p-8"
              style={{
                border: "1px solid rgba(29,52,97,0.08)",
                boxShadow: "0 12px 32px -20px rgba(29,52,97,0.18)",
              }}
            >
              <div
                aria-hidden="true"
                className="mb-6"
                style={{
                  width: 32,
                  height: 2,
                  background: "#C4564A",
                  borderRadius: 999,
                }}
              />
              <p
                style={{
                  fontFamily: monoFamily,
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "rgba(29,52,97,0.55)",
                  marginBottom: 12,
                }}
              >
                Standard celebration
              </p>
              <p
                style={{
                  fontFamily: displayFamily,
                  fontSize: "clamp(2.2rem, 4vw, 2.8rem)",
                  fontWeight: 500,
                  lineHeight: 1,
                  color: "#1D3461",
                  margin: 0,
                  letterSpacing: "-0.02em",
                }}
              >
                from{" "}
                <em
                  style={{
                    fontStyle: "italic",
                    fontWeight: 500,
                    color: "#C4564A",
                  }}
                >
                  $99
                </em>
              </p>
              <p
                className="mt-1"
                style={{
                  fontFamily: monoFamily,
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "rgba(45,41,38,0.5)",
                }}
              >
                per celebration
              </p>
              <p
                className="mt-5 text-sm leading-relaxed"
                style={{
                  fontFamily: sansFamily,
                  color: "rgba(45,41,38,0.72)",
                }}
              >
                Pick from the multi-vendor catalogue: cakes, hampers, flowers,
                spa, workshops, coffee, experiences. Real products from
                independent Australian makers. Delivery, signed card, dietary
                coordination, all included.
              </p>
              <div className="mt-7 flex flex-col gap-2">
                <Link
                  to="/browse"
                  className="inline-flex items-center justify-center gap-1.5 rounded-full border-2 border-navy/15 bg-white px-5 py-2.5 text-sm font-semibold text-navy hover:border-coral hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral"
                  style={{ fontFamily: sansFamily }}
                >
                  Browse the catalogue
                  <ArrowRight size={13} strokeWidth={2.5} />
                </Link>
                <Link
                  to="/calculator"
                  className="inline-flex items-center justify-center gap-1.5 text-xs text-navy/65 underline-offset-4 hover:underline hover:text-coral"
                  style={{ fontFamily: sansFamily }}
                >
                  Or estimate your annual cost →
                </Link>
              </div>
            </article>

            {/* Concierge route — slightly heavier visual treatment */}
            <article
              className="flex flex-col rounded-2xl p-7 md:p-8 relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(160deg, #1D3461 0%, #1D3461 70%, #2a4a85 100%)",
                color: "#FAF6F0",
                boxShadow: "0 16px 40px -20px rgba(29,52,97,0.45)",
              }}
            >
              {/* Decorative warmth */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse 480px 280px at 90% 10%, rgba(247,232,164,0.08), transparent 60%)",
                }}
              />
              <div className="relative">
                <div
                  aria-hidden="true"
                  className="mb-6"
                  style={{
                    width: 32,
                    height: 2,
                    background: "#F7E8A4",
                    borderRadius: 999,
                  }}
                />
                <p
                  style={{
                    fontFamily: monoFamily,
                    fontSize: 11,
                    fontWeight: 500,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: "rgba(247,232,164,0.85)",
                    marginBottom: 12,
                  }}
                >
                  Anything bigger
                </p>
                <p
                  style={{
                    fontFamily: displayFamily,
                    fontSize: "clamp(1.85rem, 3.4vw, 2.4rem)",
                    fontWeight: 500,
                    lineHeight: 1.1,
                    color: "#FAF6F0",
                    margin: 0,
                    letterSpacing: "-0.018em",
                  }}
                >
                  Talk to the concierge —{" "}
                  <em
                    style={{
                      fontStyle: "italic",
                      fontWeight: 500,
                      color: "#F7E8A4",
                    }}
                  >
                    we'll quote
                  </em>
                  .
                </p>
                <p
                  className="mt-5 text-sm leading-relaxed"
                  style={{
                    fontFamily: sansFamily,
                    color: "rgba(250,246,240,0.78)",
                  }}
                >
                  Premium hampers, custom curation, multi-vendor spreads, bigger
                  groups, special dietary briefs, anything outside the catalogue.
                  Brief Lembra in chat. We price it for you, you decide.
                </p>
                <div className="mt-7 flex flex-col gap-2">
                  <Link
                    to="/chat"
                    className="inline-flex items-center justify-center gap-1.5 rounded-full bg-coral px-5 py-2.5 text-sm font-semibold text-white hover:bg-coral/90 hover:-translate-y-px transition-all focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2 focus:ring-offset-navy"
                    style={{
                      fontFamily: sansFamily,
                      boxShadow: "0 4px 12px -4px rgba(196,86,74,0.4)",
                    }}
                  >
                    <MessageCircle size={13} strokeWidth={2.5} />
                    Open the concierge
                  </Link>
                </div>
              </div>
            </article>
          </div>
        </section>

        {/* ── What's included ── */}
        <section
          className="px-6 md:px-8"
          style={{
            background: "white",
            paddingTop: "clamp(56px, 7vw, 80px)",
            paddingBottom: "clamp(56px, 7vw, 80px)",
            borderTop: "1px solid rgba(29,52,97,0.06)",
            borderBottom: "1px solid rgba(29,52,97,0.06)",
          }}
        >
          <div style={{ maxWidth: 880, margin: "0 auto" }}>
            <div
              style={{
                fontFamily: monoFamily,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(196,86,74,0.85)",
                marginBottom: 12,
              }}
            >
              In every Lembra celebration
            </div>
            <h2
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(1.85rem, 3.4vw, 2.4rem)",
                lineHeight: 1.08,
                letterSpacing: "-0.018em",
                color: "#1D3461",
                margin: 0,
              }}
            >
              The price covers{" "}
              <em style={{ fontStyle: "italic", color: "#C4564A" }}>everything</em>.
            </h2>
            <ul className="mt-7 grid gap-3 sm:grid-cols-2 sm:gap-4">
              {INCLUDED.map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <span
                    className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                    style={{ background: "rgba(196,86,74,0.12)" }}
                  >
                    <Check size={11} className="text-coral" strokeWidth={2.5} />
                  </span>
                  <span
                    className="text-sm text-navy/80"
                    style={{ fontFamily: sansFamily, lineHeight: 1.5 }}
                  >
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section
          className="px-6 md:px-8"
          style={{
            paddingTop: "clamp(56px, 7vw, 80px)",
            paddingBottom: "clamp(56px, 7vw, 80px)",
          }}
        >
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <div
              style={{
                fontFamily: monoFamily,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(196,86,74,0.85)",
                marginBottom: 12,
              }}
            >
              Common questions
            </div>
            <h2
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(1.85rem, 3.4vw, 2.4rem)",
                lineHeight: 1.08,
                letterSpacing: "-0.018em",
                color: "#1D3461",
                margin: 0,
              }}
            >
              The bits people{" "}
              <em style={{ fontStyle: "italic", color: "#C4564A" }}>actually ask</em>.
            </h2>

            <div className="mt-8 space-y-5">
              {FAQ.map((item, i) => (
                <details
                  key={i}
                  className="group rounded-xl bg-white px-5 py-4"
                  style={{ border: "1px solid rgba(29,52,97,0.08)" }}
                >
                  <summary
                    className="cursor-pointer list-none text-base font-semibold text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-coral rounded"
                    style={{ fontFamily: sansFamily }}
                  >
                    {item.q}
                  </summary>
                  <p
                    className="mt-3 text-sm leading-relaxed text-navy/75"
                    style={{ fontFamily: sansFamily }}
                  >
                    {item.a}
                  </p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ── Closing CTA ── */}
        <section
          className="px-6 md:px-8 text-center"
          style={{
            background: "#1D3461",
            color: "#FAF6F0",
            paddingTop: "clamp(56px, 7vw, 80px)",
            paddingBottom: "clamp(56px, 7vw, 80px)",
          }}
        >
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <div
              aria-hidden="true"
              className="mx-auto mb-7"
              style={{
                width: 48,
                height: 2,
                background: "#C4564A",
                borderRadius: 999,
              }}
            />
            <h2
              style={{
                fontFamily: displayFamily,
                fontWeight: 500,
                fontSize: "clamp(2rem, 4vw, 2.8rem)",
                lineHeight: 1.08,
                letterSpacing: "-0.018em",
                color: "#FAF6F0",
                margin: "0 auto 14px",
                maxWidth: "20ch",
              }}
            >
              Send your first{" "}
              <em style={{ fontStyle: "italic", color: "#F7E8A4" }}>celebration</em>.
            </h2>
            <p
              style={{
                fontFamily: sansFamily,
                fontSize: 17,
                lineHeight: 1.55,
                color: "rgba(250,246,240,0.72)",
                margin: "0 auto 32px",
                maxWidth: "44ch",
              }}
            >
              Tell our concierge who's celebrating. We'll handle the rest.
            </p>
            <Link
              to="/chat"
              className="inline-flex items-center gap-2 rounded-full bg-coral px-7 py-3.5 text-sm font-medium text-white transition-all hover:bg-coral/90 hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
              style={{
                fontFamily: sansFamily,
                boxShadow: "0 4px 12px -4px rgba(196,86,74,0.4)",
              }}
            >
              Open the concierge
              <ArrowUp size={14} strokeWidth={2.5} style={{ transform: "rotate(45deg)" }} />
            </Link>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer style={{ background: "#1D3461", padding: "0 0 36px" }}>
          <div
            className="mx-auto px-6 md:px-8 border-t border-white/10 pt-10 flex flex-col items-center gap-3"
            style={{ maxWidth: 1180 }}
          >
            <Logo size="sm" onDark />
            <p className="text-xs text-white/50" style={{ fontFamily: sansFamily }}>
              © 2026 Lembra. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
